'use client';

import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useState } from 'react';

import { ScheduleProduct } from '@/apis/tds';
import { doTranslation } from '@/app/i18n/client';
import { default as Accordion_ } from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import styles from '@/components/schedule-results/scheduleResultsFAQsAccordion.module.scss';

import chevronDarkDown from '@/public/assets/images/global/chevron-dark-down-faq.svg';
import chevronDarkUp from '@/public/assets/images/global/chevron-dark-up-faq.svg';

type Props = {
  averageTravelTime: number | null;
  averageTravelTimeFormatted: string;
  destinationCityName: string;
  earliestSchedule: ScheduleProduct | null;
  latestSchedule: ScheduleProduct | null;
  lng: string;
  originCityName: string;
  shortestTravelTime: number | null;
  earliestDepartTimeFormatted: string;
  earliestArrivalTimeFormatted: string;
  latestDepartTimeFormatted: string;
  latestArrivalTimeFormatted: string;
};

const ScheduleResultsFAQsAccordion = (props: Props) => {
  const [expanded, setExpanded] = useState('');
  const { t } = doTranslation(props.lng);

  const faqs: { [key: string]: { summary: string; details: string[] } } = {
    ...(props.averageTravelTimeFormatted && {
      'faq-how-long': {
        summary: t('How long does it take to get from {{originCity}} to {{destinationCity}}?', {
          originCity: props.originCityName,
          destinationCity: props.destinationCityName
        }),
        details: [
          t(
            'The travel time from {{originCity}} to  {{destinationCity}} can vary depending on several factors, such as the specific route, traffic conditions, and any stops ' +
              'along the way. On average, the journey typically takes around {{averageTravelTime}}, but we recommend checking the exact duration when you book your ticket. Please ' +
              "keep in mind that travel times may change due to unforeseen circumstances, so it's always a good idea to allow some flexibility in your plans. Safe travels!",
            {
              averageTravelTime: props.averageTravelTimeFormatted,
              originCity: props.originCityName,
              destinationCity: props.destinationCityName
            }
          )
        ]
      }
    }),
    ...(props.earliestSchedule &&
      props.latestSchedule && {
        'faq-earliest-and-latest': {
          summary: t('What are the earliest and latest bus trips {{originCity}} to {{destinationCity}}?', {
            originCity: props.originCityName,
            destinationCity: props.destinationCityName
          }),
          details: [
            t(
              'The earliest and latest bus trips from {{originCity}} to {{destinationCity}} can vary depending on the day of the week and specific schedules. ' +
                'Typically, the earliest bus departs from {{originCity}} around {{earliestDepartTime}} and arrives at {{destinationCity}} around {{earliestArrivalTime}}. The latest bus ' +
                'from {{originCity}} to {{destinationCity}} usually leaves at approximately {{latestDepartTime}} and arrives at {{destinationCity}} around {{latestArrivalTime}}.',
              {
                originCity: props.originCityName,
                destinationCity: props.destinationCityName,
                earliestDepartTime: props.earliestDepartTimeFormatted,
                earliestArrivalTime: props.earliestArrivalTimeFormatted,
                latestDepartTime: props.latestDepartTimeFormatted,
                latestArrivalTime: props.latestArrivalTimeFormatted
              }
            ),
            t(
              'Keep in mind that these times are subject to change, so we recommend visiting our website or contacting our customer service for the most up-to-date and ' +
                "specific information regarding the schedules for your preferred travel date. We're here to help you plan your journey, no matter what time you choose to travel."
            )
          ]
        }
      }),
    ...(props.shortestTravelTime && {
      'faq-bus-stations': {
        summary: t('What bus station(s) are closest to {{originCity}}?', {
          originCity: props.originCityName
        }),
        details: [
          t(
            'The bus station closest to {{originCity}} depends on your exact location within the area. Trailways serves multiple bus stations in various regions, ' +
              'and our aim is to provide convenient access for our passengers.',
            {
              originCity: props.originCityName
            }
          ),
          t(
            'To find the nearest bus station to your location, we recommend visiting our ' +
              "website and using our station locator tool. By entering your address or location details, you'll receive information about the closest bus stations, " +
              'their addresses, and any additional details you might need, such as amenities and services available at each station. Alternatively, our friendly customer ' +
              'service team is always ready to assist you in finding the most convenient options for your journey.'
          )
        ]
      }
    }),
    'faq-reschedule': {
      summary: t("How can I reschedule my trip if I've bought my ticket through {{siteName}}?", {
        siteName: process.env.NEXT_PUBLIC_SITE_PRETTY_NAME
      }),
      details: [
        t(
          "Rescheduling your trip when you've purchased your ticket through {{siteName}} is a hassle-free process. Simply log in to your account, access your bookings, " +
            "and select the options that best align with your updated travel plans. Review your choices, confirm the changes, and you'll receive a revised itinerary for your upcoming " +
            "journey. Please be aware that rescheduling policies can vary based on your ticket type and the time remaining before your trip, so it's advisable to consult our website " +
            'for specific details. Our customer support team is also available to assist you with any questions or challenges you may encounter, ensuring your experience with Trailways ' +
            'remains flexible and convenient.',
          { siteName: process.env.NEXT_PUBLIC_SITE_PRETTY_NAME }
        )
      ]
    }
  };

  const renderAccordion = (id: string) => {
    if (!faqs[id]) {
      return null;
    }
    const isExpanded = expanded === id;
    return (
      <Accordion_
        expanded={isExpanded}
        disableGutters={true}
        sx={{
          'alignItems': 'flex-start',
          'alignSelf': 'stretch',
          'borderRadius': 'calc(4rem / var(--font-factor))',
          'display': 'flex',
          'flexDirection': 'column',
          '& .MuiButtonBase-root.MuiAccordionSummary-root.Mui-focusVisible': {
            border: 'calc(3rem / var(--font-factor)) solid #213139',
            background: isExpanded ? 'var(--primary-2, #E7F9FF)' : 'white'
          },
          '& .MuiButtonBase-root.MuiAccordionSummary-root': {
            'padding': 'calc(24rem / var(--font-factor))',
            '@media (max-width: 600px)': {
              padding: 'calc(24rem / var(--font-factor)) calc(16rem / var(--font-factor))'
            }
          },
          '&.MuiAccordion-root': {
            boxShadow: 'none'
          },
          'borderBottom': 'calc(1rem / var(--font-factor)) solid #EEE'
        }}
      >
        <AccordionSummary
          aria-controls={`content-${id}`}
          aria-expanded={isExpanded}
          id={`accordion-control-${id}`}
          onClick={() => setExpanded(isExpanded ? '' : id ?? '')}
          expandIcon={
            <div
              style={{
                alignItems: 'center',
                display: 'flex',
                gap: 'calc(8rem / var(--font-factor))',
                justifyContent: 'center'
              }}
            >
              <Image
                aria-hidden="true"
                alt={isExpanded ? t('up arrow') : t('down arrow')}
                height={36}
                src={isExpanded ? chevronDarkUp : chevronDarkDown}
                width={36}
              />
            </div>
          }
          sx={{
            'alignItems': 'center',
            'alignSelf': 'stretch',
            'border': 'calc(3rem / var(--font-factor)) solid transparent',
            'background': isExpanded ? 'var(--primary-2, #E7F9FF)' : 'inherit',
            'borderRadius': isExpanded
              ? 'calc(4rem / var(--font-factor)) calc(4rem / var(--font-factor)) calc(0rem / var(--font-factor)) calc(0rem / var(--font-factor))'
              : 'calc(4rem / var(--font-factor))',
            'display': 'flex',
            'gap': 'calc(24rem / var(--font-factor))',
            'marginTop': 'calc(-1rem / var(--font-factor))',
            '& .MuiAccordionSummary-content': {
              margin: 'calc(0rem / var(--font-factor))'
            }
          }}
        >
          <Typography
            sx={{
              'flex': '1 0 0',
              'color': 'var(--black-0, #213139)',
              'fontFamily': 'var(--font-inter)',
              'fontSize': 'calc(18rem / var(--font-factor))',
              'fontStyle': 'normal',
              'fontWeight': isExpanded ? '700' : '400',
              'lineHeight': 'calc(24rem / var(--font-factor))',
              '@media (max-width: 1279px)': {
                fontSize: 'calc(16rem / var(--font-factor))',
                lineHeight: 'calc(24rem / var(--font-factor))'
              }
            }}
          >
            {faqs[id].summary}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          aria-labelledby={`accordion-control-${id}`}
          aria-hidden={!isExpanded}
          id={`content-for-${id}`}
          role="region"
          sx={{
            'alignItems': 'flex-start',
            'alignSelf': 'stretch',
            'display': 'flex',
            'gap': 'calc(24rem / var(--font-factor))',
            'padding':
              'calc(16rem / var(--font-factor)) calc(24rem / var(--font-factor)) calc(24rem / var(--font-factor)) calc(24rem / var(--font-factor))',
            '@media (max-width: 600px)': {
              padding:
                'calc(16rem / var(--font-factor)) calc(16rem / var(--font-factor)) calc(24rem / var(--font-factor)) calc(16rem / var(--font-factor))'
            }
          }}
        >
          {faqs[id].details.length > 1 ? (
            <div className={styles.text}>
              {faqs[id].details.map((item, index) => {
                return <p key={index}>{item}</p>;
              })}
            </div>
          ) : (
            <div className={styles.text}>{faqs[id].details}</div>
          )}
        </AccordionDetails>
      </Accordion_>
    );
  };

  return (
    <ul className={styles.list} aria-label={t('Frequently Asked Questions Accordion Control Button Group')}>
      {props.averageTravelTime && <li>{renderAccordion('faq-how-long')}</li>}
      {props.earliestSchedule && props.latestSchedule && <li>{renderAccordion('faq-earliest-and-latest')}</li>}
      {props.shortestTravelTime && <li>{renderAccordion('faq-bus-stations')}</li>}
      <li>{renderAccordion('faq-reschedule')}</li>
    </ul>
  );
};
export default ScheduleResultsFAQsAccordion;
